/**
 * Workspace API Service
 * Handles fetching workspace (tenant) data for the current user
 */

import { ApiService } from './api';
import { getWorkspaceColor } from '../utils/workspaceColors';
import type { Workspace } from '../types/workspace';

/**
 * API response from backend for tenant list
 */
interface TenantListResponse {
  id: number;
  tenant_id: string;
  tenant_name: string;
  domain: string;
  status: string;
  has_access: boolean;
  notes: string | null;
}

export const workspaceApi = {
  /**
   * List all workspaces accessible to the current user
   * Maps tenant data to workspace format with auto-assigned colors
   */
  async listWorkspaces(): Promise<Workspace[]> {
    const response = await ApiService.request<{ items: TenantListResponse[] }>(
      '/api/tenants',
      { method: 'GET' }
    );

    // Map tenant data to workspace format
    return response.items
      .filter((tenant) => tenant.status === 'managed')
      .map((tenant) => ({
        id: tenant.tenant_id,
        name: tenant.tenant_name,
        domain: tenant.domain,
        color: getWorkspaceColor(tenant.tenant_id),
        hasUserAccess: tenant.has_access ?? false,
        notes: tenant.notes,
      }));
  },

  /**
   * Get a single workspace by ID
   */
  async getWorkspace(workspaceId: string): Promise<Workspace> {
    // Get all workspaces and find the matching one
    // This ensures consistent color assignment
    const workspaces = await this.listWorkspaces();
    const workspace = workspaces.find((w) => w.id === workspaceId);

    if (!workspace) {
      throw new Error(`Workspace ${workspaceId} not found`);
    }

    return workspace;
  },
};
